// Indstillinger: lyd, grafik, bevægelse og trænerens hånd. Alt gemmes med det samme.

import type { MotionMode, QualityMode } from '../../storage';
import { h } from '../dom';
import { ICON } from '../icons';

export interface SettingsValues {
  muted: boolean;
  vol: { master: number; music: number; sfx: number };
  quality: QualityMode;
  /** Det trin, auto har valgt lige nu (vises ved Auto). */
  qualityNow: string;
  motion: MotionMode;
  /** Systemet beder om mindre bevægelse. */
  systemCalm: boolean;
  /** Vibration til, og kan telefonen overhovedet (ellers vises rækken ikke). */
  haptics: boolean;
  canVibrate: boolean;
}

export interface SettingsHandlers {
  muted(m: boolean): void;
  volumes(v: { master: number; music: number; sfx: number }): void;
  quality(q: QualityMode): void;
  motion(m: MotionMode): void;
  haptics(on: boolean): void;
  resetHand(): void;
  back(): void;
}

const QUALITY: Array<[QualityMode, string]> = [
  ['auto', 'Auto'],
  ['low', 'Lav'],
  ['mid', 'Mellem'],
  ['high', 'Høj'],
];

const MOTION: Array<[MotionMode, string]> = [
  ['auto', 'Auto'],
  ['calm', 'Rolig'],
  ['full', 'Fuld'],
];

/** Lydknappen siger både med ikon og ord, om lyden er til eller fra. */
function soundFace(on: boolean): string {
  return `${on ? ICON.sound : ICON.muted}<span>${on ? 'Til' : 'Fra'}</span>`;
}

const TIER_NAME: Record<string, string> = { low: 'lav', mid: 'mellem', high: 'høj' };

function radios<T extends string>(label: string, opts: Array<[T, string]>, value: T, pick: (v: T) => void): HTMLElement {
  const btns: HTMLButtonElement[] = [];
  const row = h('div', { class: 'seg', role: 'radiogroup', 'aria-label': label });
  for (const [id, text] of opts) {
    const b = h(
      'button',
      {
        type: 'button',
        role: 'radio',
        class: 'seg-btn',
        'aria-checked': String(id === value),
        onclick: () => {
          for (const x of btns) x.setAttribute('aria-checked', String(x === b));
          pick(id);
        },
      },
      text,
    );
    btns.push(b);
    row.append(b);
  }
  return row;
}

export function settingsScreen(v: SettingsValues, handlers: SettingsHandlers): HTMLElement {
  const vol = { ...v.vol };
  const slider = (key: keyof typeof vol, label: string): HTMLElement => {
    const out = h('output', {}, `${Math.round(vol[key] * 100)}`);
    const input = h('input', {
      type: 'range',
      min: 0,
      max: 100,
      step: 5,
      value: Math.round(vol[key] * 100),
      'aria-label': label,
      style: `--pct: ${Math.round(vol[key] * 100)}%`,
      oninput: (e: Event) => {
        const el = e.target as HTMLInputElement;
        const n = Number(el.value) / 100;
        vol[key] = n;
        out.textContent = `${Math.round(n * 100)}`;
        el.style.setProperty('--pct', `${Math.round(n * 100)}%`);
        handlers.volumes({ ...vol });
      },
    });
    return h('label', { class: 'slider' }, h('span', {}, label), input, out);
  };
  const soundBtn = h('button', {
    type: 'button',
    class: 'switch',
    role: 'switch',
    'aria-checked': String(!v.muted),
    onclick: () => {
      const on = soundBtn.getAttribute('aria-checked') !== 'true';
      soundBtn.setAttribute('aria-checked', String(on));
      soundBtn.innerHTML = soundFace(on);
      handlers.muted(!on);
    },
    html: soundFace(!v.muted),
    'aria-label': 'Lyd',
  });
  const onOff = (on: boolean): string => `<span>${on ? 'Til' : 'Fra'}</span>`;
  const buzzBtn = h('button', {
    type: 'button',
    class: 'switch',
    role: 'switch',
    'aria-checked': String(v.haptics),
    onclick: () => {
      const on = buzzBtn.getAttribute('aria-checked') !== 'true';
      buzzBtn.setAttribute('aria-checked', String(on));
      buzzBtn.innerHTML = onOff(on);
      handlers.haptics(on);
    },
    html: onOff(v.haptics),
    'aria-label': 'Vibration',
  });
  const handBtn = h(
    'button',
    {
      type: 'button',
      class: 'btn ghost',
      onclick: () => {
        handlers.resetHand();
        handBtn.replaceChildren(h('span', { html: ICON.hand }), 'Hånden er klar igen');
        handBtn.setAttribute('disabled', '');
      },
    },
    h('span', { html: ICON.hand }),
    'Vis hånden igen',
  );
  return h(
    'div',
    { class: 'card settings' },
    h('h1', { class: 'title small' }, 'INDSTILLINGER'),
    h('h2', { class: 'section row' }, 'Lyd', soundBtn),
    h('div', { class: 'sliders' }, slider('master', 'Samlet'), slider('music', 'Musik'), slider('sfx', 'Effekter')),
    h('h2', { class: 'section' }, 'Grafik'),
    radios('Grafik', QUALITY, v.quality, (q) => handlers.quality(q)),
    h('p', { class: 'help' }, v.quality === 'auto' ? `Auto skifter selv efter billedraten. Lige nu: ${TIER_NAME[v.qualityNow] ?? v.qualityNow}.` : 'Høj giver glød på gnister og fyrværkeri.'),
    h('h2', { class: 'section' }, 'Bevægelse'),
    radios('Bevægelse', MOTION, v.motion, (m) => handlers.motion(m)),
    h('p', { class: 'help' }, v.systemCalm ? 'Auto er rolig, fordi din enhed beder om mindre bevægelse.' : 'Rolig slår ryst, hop og frys fra.'),
    v.canVibrate ? h('h2', { class: 'section row' }, 'Vibration', buzzBtn) : null,
    h('h2', { class: 'section' }, 'Hånden'),
    handBtn,
    h(
      'div',
      { class: 'actions' },
      h('button', { type: 'button', class: 'btn primary', autofocus: true, onclick: () => handlers.back() }, 'Færdig'),
    ),
  );
}
